<script lang="ts">
  import { createEventDispatcher } from 'svelte';
  import { gfxQuality, GFX_ORDER, PRESETS } from './gfxSettings';
  export let hasSave = false;
  const emit = createEventDispatcher();
</script>

<div class="menu" role="dialog" aria-label="Cloud Nine — main menu">
  <div class="scrim"></div>
  <div class="content">
    <div class="eyebrow">A CITY FOR THE TAKING</div>
    <h1 class="title">
      <span class="cloud">☁</span><span class="word">CLOUD</span><span class="nine">9</span>
    </h1>
    <p class="tagline">Build a drug empire one building at a time — grow, deal, and take the city block by block.</p>

    <div class="actions">
      {#if hasSave}
        <button class="btn primary" on:click={() => emit('continue')}>
          <span class="ico">▶</span> Continue
        </button>
        <button class="btn ghost" on:click={() => emit('newgame')}>New Game</button>
      {:else}
        <button class="btn primary" on:click={() => emit('newgame')}>
          <span class="ico">＋</span> New Game
        </button>
      {/if}
    </div>

    <div class="gfx">
      <span class="gfx-label">GRAPHICS</span>
      <div class="segment" role="group" aria-label="Graphics quality">
        {#each GFX_ORDER as q}
          <button
            class="seg"
            class:on={$gfxQuality === q}
            aria-pressed={$gfxQuality === q}
            title={q === 'auto' ? 'Adapts resolution to keep the frame rate up' : `${PRESETS[q].label} quality`}
            on:click={() => gfxQuality.set(q)}
          >{PRESETS[q].label}</button>
        {/each}
      </div>
    </div>

    <div class="foot">Fictional &amp; satirical · M17+</div>
  </div>
</div>

<style>
  .menu {
    position: absolute;
    inset: 0;
    z-index: 100;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    animation: fade 0.45s ease both;
  }
  /* Cinematic veil: subdues the live city behind so the title reads cleanly, while a soft centre
     window keeps the diorama visible. Heavier top & bottom, with a warm-black tint to match dusk. */
  .scrim {
    position: absolute;
    inset: 0;
    pointer-events: none;
    background:
      radial-gradient(115% 78% at 50% 44%, rgba(7, 9, 14, 0.28) 20%, rgba(6, 8, 13, 0.62) 62%, rgba(4, 5, 9, 0.9) 100%),
      linear-gradient(180deg, rgba(6, 8, 13, 0.85) 0%, rgba(8, 9, 14, 0.42) 30%, rgba(8, 9, 14, 0.42) 62%, rgba(4, 5, 9, 0.94) 100%);
    backdrop-filter: saturate(1.1) blur(1.5px);
  }
  .content {
    position: relative;
    text-align: center;
    padding: 24px;
    animation: rise 0.6s cubic-bezier(0.16, 1, 0.3, 1) both;
  }
  .eyebrow {
    font: 700 12px var(--font);
    letter-spacing: 6px;
    color: var(--ink-dim);
    margin-bottom: 10px;
    text-shadow: 0 1px 6px #000;
  }
  .title {
    display: flex;
    align-items: baseline;
    justify-content: center;
    gap: 10px;
    margin: 0;
    font: 900 clamp(52px, 12vw, 108px) var(--font);
    line-height: 0.9;
    letter-spacing: 2px;
  }
  .title .cloud {
    font-size: 0.62em;
    color: #cfe9ff;
    transform: translateY(-0.12em);
    filter: drop-shadow(0 0 18px rgba(120, 200, 255, 0.55));
    animation: bob 6s ease-in-out infinite;
  }
  .title .word {
    color: #f4f7ff;
    text-shadow: 0 2px 2px rgba(0, 0, 0, 0.6), 0 0 30px rgba(160, 200, 255, 0.25);
  }
  .title .nine {
    /* neon green "9" — the empire accent */
    color: #4dff9a;
    text-shadow:
      0 0 8px rgba(77, 255, 154, 0.9),
      0 0 26px rgba(57, 211, 83, 0.7),
      0 0 60px rgba(57, 211, 83, 0.45);
    animation: flicker 5.5s infinite;
  }
  .tagline {
    max-width: 30ch;
    margin: 16px auto 0;
    font: 500 15px/1.5 var(--font);
    color: var(--ink-dim);
    text-shadow: 0 1px 5px #000;
  }
  .actions {
    display: flex;
    flex-direction: column;
    gap: 12px;
    align-items: center;
    margin-top: 34px;
  }
  .btn {
    min-width: 240px;
    padding: 14px 26px;
    border-radius: 12px;
    font: 800 16px var(--font);
    letter-spacing: 0.5px;
    cursor: pointer;
    transition: transform 0.12s ease, box-shadow 0.2s ease, background 0.2s ease, border-color 0.2s ease;
  }
  .btn .ico {
    margin-right: 8px;
    font-size: 0.9em;
  }
  .btn.primary {
    color: #06210f;
    background: linear-gradient(180deg, #58f39c, #2ecf76);
    border: 1px solid rgba(120, 255, 180, 0.6);
    box-shadow: 0 8px 26px rgba(46, 207, 118, 0.35), 0 0 0 1px rgba(0, 0, 0, 0.2) inset;
  }
  .btn.primary:hover {
    transform: translateY(-2px);
    box-shadow: 0 12px 34px rgba(46, 207, 118, 0.5);
  }
  .btn.ghost {
    color: var(--ink);
    background: rgba(18, 22, 30, 0.5);
    border: 1px solid var(--line);
    backdrop-filter: blur(6px);
  }
  .btn.ghost:hover {
    transform: translateY(-2px);
    border-color: rgba(160, 200, 255, 0.5);
    background: rgba(28, 34, 46, 0.6);
  }
  .btn:active {
    transform: translateY(0);
  }
  .gfx {
    margin-top: 26px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
  }
  .gfx-label {
    font: 700 10px var(--font);
    letter-spacing: 4px;
    color: var(--ink-faint);
    text-shadow: 0 1px 4px #000;
  }
  .segment {
    display: inline-flex;
    padding: 3px;
    border-radius: 11px;
    background: rgba(14, 17, 24, 0.55);
    border: 1px solid var(--line);
    backdrop-filter: blur(6px);
  }
  .seg {
    min-width: 62px;
    padding: 7px 12px;
    border: none;
    border-radius: 8px;
    background: none;
    color: var(--ink-dim);
    font: 700 12.5px var(--font);
    cursor: pointer;
    transition: color 0.15s ease, background 0.15s ease;
  }
  .seg:hover {
    color: var(--ink);
  }
  .seg.on {
    color: #06210f;
    background: linear-gradient(180deg, #58f39c, #2ecf76);
    box-shadow: 0 2px 10px rgba(46, 207, 118, 0.35);
  }
  .foot {
    margin-top: 22px;
    font: 600 10.5px var(--font);
    letter-spacing: 2px;
    color: var(--ink-faint);
    text-shadow: 0 1px 4px #000;
  }

  @keyframes fade {
    from { opacity: 0; }
    to { opacity: 1; }
  }
  @keyframes rise {
    from { opacity: 0; transform: translateY(22px); }
    to { opacity: 1; transform: translateY(0); }
  }
  @keyframes bob {
    0%, 100% { transform: translateY(-0.12em); }
    50% { transform: translateY(-0.26em); }
  }
  @keyframes flicker {
    0%, 100%, 42%, 46% { opacity: 1; }
    43% { opacity: 0.72; }
    44% { opacity: 0.95; }
    45% { opacity: 0.6; }
  }
  @media (prefers-reduced-motion: reduce) {
    .menu, .content, .title .cloud, .title .nine { animation: none; }
  }
</style>
